// game/tick.js
// Main game loop and tick processing

import { GAME_CONFIG, FARM_CONFIG } from './constants.js';
import logger from '../utils/logger.js';
import { IS_DEV } from '../config/env.js';
import { gameState } from './gameState.js';
import { skillRegistry, getSkillIcon } from '../core/skillRegistry.js';
import { adminPanel } from '../skills/admin/admin.js';
import { getCurrentSkillId } from '../core/routeSetup.js';
import { updateInventoryDisplay } from '../ui/inventoryUI.js';
import { updateEquipmentDisplay } from '../ui/equipmentUI.js';
import { farmingSkill } from '../skills/farming/farming.js';
import { husbandrySkill } from '../skills/husbandry/husbandry.js';
import { bountySkill } from '../skills/bounty/bounty.js';
import { pilferingSkill } from '../skills/pilfering/pilfering.js';
import { architectingSkill } from '../skills/architecting/architecting.js';
import { BOUNTY_CONFIG, getBountyXP } from '../skills/bounty/bountyData.js';
import { getItemName, getItemIcon } from '../data/items/index.js';
import { formatNumber } from '../utils/format.js';
import { combatSkill } from '../skills/combat/combat.js';
import { updateSidebarNotification } from '../skills/husbandry/husbandryUI.js';
import { updatePilferingSidebarNotification } from '../skills/pilfering/pilferingUI.js';
import { updateCorruptionTimer, isCorruptionActive, getCorruptionState, getCorruptionSpeedMultiplier, isCorruptibleSkill, getCorruptedSkillIds, getSkillCorruptionInfo } from '../systems/corruption.js';

let gameLoopInterval = null;

/**
 * Main game tick - called every TICK_RATE ms
 */
function gameTick() {
    // Get speed multiplier from admin panel (dev only)
    const speedMultiplier = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;

    // Increment tick counter
    gameState.tick();

    // Update corruption timer
    const tickMs = GAME_CONFIG.TICK_RATE * speedMultiplier;
    updateCorruptionTimer(tickMs);

    // Process current action if any
    const action = gameState.getCurrentAction();
    if (action && action.tick) {
        // Apply corruption speed multiplier for corruptible skills
        let totalSpeedMultiplier = speedMultiplier;
        if (action.type && isCorruptibleSkill(action.type)) {
            totalSpeedMultiplier *= getCorruptionSpeedMultiplier(action.type);
        }
        // Note: talent speed bonus is already baked into action.duration via calculateEffectiveSpeed()
        // Pass speed multiplier to action tick for proper elapsed time calculation
        action.tick(totalSpeedMultiplier);
    }

    // Process HoT effect even when not in combat
    // (Combat tick handles HoT when fighting, this handles it when idle)
    if (!action || action.type !== 'combat') {
        combatSkill.processHotEffect();
    }

    // Check farm plots for completion (passive growth)
    checkFarmPlots();

    // Check pet contracts for completion (passive contracts)
    checkPetContracts();

    // Check pilfering guild (passive gathering)
    checkPilferingGuild(speedMultiplier);

    // Check architecting milestones and civic contracts (passive building)
    checkArchitectingProgress();

    // Update UI
    updateUI();

    // Update corruption visual effects
    updateCorruptionVisuals();
}

/**
 * Check all farm plots for growth completion
 */
function checkFarmPlots() {
    const plots = gameState.getFarmPlots();
    if (!plots) return;

    for (let i = 0; i < FARM_CONFIG.MAX_PLOTS; i++) {
        const plot = plots[i];
        if (plot && plot.unlocked && plot.state === 'growing') {
            farmingSkill.checkPlotProgress(i);
        }
    }
}

/**
 * Check all pet contracts for completion
 */
function checkPetContracts() {
    const completedPets = husbandrySkill.checkAllContractCompletions();
    for (const { petId, slot } of completedPets) {
        husbandrySkill.completeContract(petId, slot);
    }
}

/**
 * Check pilfering guild for passive gathering
 */
function checkPilferingGuild(speedMultiplier) {
    if (gameState.isGuildUnlocked()) {
        pilferingSkill.tickGuild(speedMultiplier);
    }
}

/**
 * Check architecting milestones and civic contracts for passive progress
 */
function checkArchitectingProgress() {
    // Tick active blueprint (passive, runs alongside other actions)
    architectingSkill.tickBlueprint();

    // Tick active milestone (runs alongside other actions)
    architectingSkill.tickMilestone();

    // Tick active civic contract
    architectingSkill.tickCivicContract();
}

/**
 * Update UI elements that change each tick
 */
function updateUI() {
    // Update action display (handles both active and cleared states)
    const action = gameState.getCurrentAction();
    updateActionDisplay(action);
    setupCurrentActionHandler();

    // Update the currently displayed skill's UI (regardless of which skill has active action)
    const currentSkillId = getCurrentSkillId();
    if (currentSkillId) {
        const skillConfig = skillRegistry.get(currentSkillId);
        if (skillConfig?.update) {
            skillConfig.update();
        }
    }

    // Update sidebar skill levels every tick
    updateSidebarLevels();

    // Update inventory display
    updateInventoryDisplay();

    // Update equipment display (if viewing equipment tab)
    updateEquipmentDisplay();

    // Update husbandry chest notification indicator
    updateSidebarNotification();

    // Update pilfering guild chest notification indicator
    updatePilferingSidebarNotification();

    // Check for newly ready bounty contracts
    bountySkill.checkAllContractsReady();

    // Update active bounty contracts display
    updateActiveContractsDisplay();
    setupContractDeliveryHandler();
}

/**
 * Update skill levels in sidebar
 */
function updateSidebarLevels() {
    const skills = skillRegistry.getAll();
    const virtualEnabled = gameState.settings.virtualLevelsEnabled;

    for (const skill of skills) {
        const skillData = gameState.getSkill(skill.id);
        if (!skillData || skill.id === 'admin') continue;

        const levelEl = document.querySelector(`[data-skill="${skill.id}"] .skill-level`);
        if (levelEl) {
            // Only show virtual level if setting is enabled and level > 99
            const virtualDisplay = (virtualEnabled && skillData.virtualLevel > 99)
                ? ` (${skillData.virtualLevel})`
                : '';
            levelEl.textContent = `Level ${skillData.level}/99${virtualDisplay}`;
        }
    }

    // Update combat HP display
    updateCombatHpDisplay();

    // Update total level in header
    updateTotalLevelDisplay();
}

/**
 * Update total level display in header
 */
function updateTotalLevelDisplay() {
    const totalLevelEl = document.getElementById('total-level');
    if (!totalLevelEl) return;

    gameState.recalculateTotalLevel();
    totalLevelEl.textContent = gameState.player.stats.totalLevel;
}

/**
 * Update HP display on combat skill tile
 * Only shows when HP is below max, color-coded by HP %
 */
function updateCombatHpDisplay() {
    const hpEl = document.querySelector('[data-skill="combat"] .skill-hp');
    if (!hpEl) return;

    const currentHp = gameState.getCurrentHp();
    const maxHp = gameState.getMaxHp();

    // Hide when at full HP
    if (currentHp >= maxHp) {
        hpEl.style.display = 'none';
        return;
    }

    // Show HP display
    hpEl.style.display = 'flex';
    hpEl.textContent = `❤️ ${currentHp}/${maxHp}`;

    // Color code based on HP percentage
    const hpPercent = (currentHp / maxHp) * 100;
    hpEl.classList.remove('hp-high', 'hp-medium', 'hp-low');

    if (hpPercent > 50) {
        hpEl.classList.add('hp-high');
    } else if (hpPercent > 25) {
        hpEl.classList.add('hp-medium');
    } else {
        hpEl.classList.add('hp-low');
    }
}

/**
 * Get the primary output item icon from an action
 * Different skills store their resource in different properties
 */
function getActionOutputIcon(action) {
    if (!action) return '';

    // Map of skill types to their resource property and drops path
    const resourceMap = {
        mining: 'rock',
        woodcutting: 'tree',
        fishing: 'fish',
        pyre: 'log',
        cooking: 'food',
        smithing: 'bar',
        woodworking: 'recipe',
        crafting: 'recipe',
        alchemy: 'recipe',
        glyphbinding: 'node',
        gatherer: 'zone',
        pilfering: 'target'
    };

    const resourceProp = resourceMap[action.type];
    if (!resourceProp) return '';

    const resource = action[resourceProp];
    if (!resource) return '';

    // Get the first output item
    // Different skills use 'drops', 'outputItem', or 'output'
    let outputItemId = null;

    if (resource.outputItem) {
        outputItemId = resource.outputItem;
    } else if (resource.output) {
        outputItemId = resource.output;
    } else if (resource.drops && resource.drops.length > 0) {
        // For drops arrays, get the first non-coins item, or first item if all coins
        const nonCoinDrop = resource.drops.find(d => d.itemId !== 'coins');
        outputItemId = nonCoinDrop ? nonCoinDrop.itemId : resource.drops[0].itemId;
    }

    if (!outputItemId) return '';

    // Get the icon for this item
    const icon = getItemIcon(outputItemId);
    return icon || '';
}

/**
 * Update current action display
 * Uses incremental updates to preserve hover/click states
 * Combat actions show mob info instead of time remaining
 */
function updateActionDisplay(action) {
    const actionDiv = document.getElementById('current-action');
    if (!action) {
        actionDiv.innerHTML = '<p class="empty-message">No active action</p>';
        return;
    }

    // Combat actions get special treatment
    if (action.type === 'combat') {
        updateCombatActionDisplay(actionDiv, action);
        return;
    }

    // Calculate time remaining
    const progress = action.progress || 0;
    const duration = action.duration || 1000;
    const elapsed = progress * duration;
    const remaining = Math.max(0, duration - elapsed);
    const remainingSeconds = (remaining / 1000).toFixed(1);

    // Build the route - include subType as tab if present
    const route = action.subType ? `${action.type}/${action.subType}` : action.type;
    const armorTier = action.armorTier || '';

    // Check if we need to rebuild or just update
    const existingInfo = actionDiv.querySelector('.action-info:not(.combat-action)');
    const currentRoute = existingInfo?.dataset.route;

    if (existingInfo && currentRoute === route) {
        // Just update the dynamic parts (time and progress)
        const timerValue = existingInfo.querySelector('.timer-value');
        const progressFill = existingInfo.querySelector('.progress-fill');
        if (timerValue) timerValue.textContent = `${remainingSeconds}s`;
        if (progressFill) progressFill.style.width = `${progress * 100}%`;
    } else {
        // Full rebuild needed (action changed)
        const skillIcon = getSkillIcon(action.type);
        const outputIcon = getActionOutputIcon(action);

        // Build output display if we have an icon
        const outputHtml = outputIcon ? `
          <div class="action-output">
            <span class="output-label">Output:</span>
            <span class="output-icon">${outputIcon}</span>
          </div>
        ` : '';

        actionDiv.innerHTML = `
        <div class="action-info clickable" data-route="${route}" data-armor-tier="${armorTier}">
          <strong>${skillIcon} ${action.name || 'Unknown Action'}</strong>
          ${outputHtml}
          <div class="action-timer">
            <span class="timer-label">Time:</span>
            <span class="timer-value">${remainingSeconds}s</span>
          </div>
          <div class="progress-bar mini">
            <div class="progress-fill" style="width: ${progress * 100}%"></div>
          </div>
        </div>
      `;
    }
}

/**
 * Update combat action display with mob info and player HP
 */
function updateCombatActionDisplay(actionDiv, action) {
    const state = combatSkill.getCombatState();
    const route = 'combat';

    // If not actually in combat (stopped fighting), show empty state
    if (!state.inCombat && !state.isDead && !state.isInKillDelay) {
        actionDiv.innerHTML = '<p class="empty-message">No active action</p>';
        return;
    }

    // Check if we need to rebuild (switching to/from combat)
    const existingInfo = actionDiv.querySelector('.action-info.combat-action');

    if (!existingInfo) {
        // Build the combat action display
        actionDiv.innerHTML = `
        <div class="action-info combat-action clickable" data-route="${route}">
          <strong>⚔️ ${action.name || 'Combat'}</strong>
          <div class="combat-player-hp">
            <span class="player-hp-label">❤️ You:</span>
            <span class="player-hp-text"></span>
            <div class="progress-bar mini player-hp-bar">
              <div class="progress-fill player-hp-fill"></div>
            </div>
          </div>
          <div class="combat-mob-info">
            <span class="mob-name"></span>
            <span class="mob-hp-text"></span>
          </div>
          <div class="progress-bar mini mob-hp-bar">
            <div class="progress-fill mob-hp-fill"></div>
          </div>
        </div>
      `;
    }

    // Update player HP
    const playerHpTextEl = actionDiv.querySelector('.player-hp-text');
    const playerHpFill = actionDiv.querySelector('.player-hp-fill');
    const playerHpPercent = state.maxHp > 0 ? (state.currentHp / state.maxHp) * 100 : 0;

    if (playerHpTextEl) playerHpTextEl.textContent = `${state.currentHp}/${state.maxHp}`;
    if (playerHpFill) {
        playerHpFill.style.width = `${playerHpPercent}%`;
        playerHpFill.classList.remove('hp-high', 'hp-mid', 'hp-low');
        if (playerHpPercent > 50) {
            playerHpFill.classList.add('hp-high');
        } else if (playerHpPercent > 25) {
            playerHpFill.classList.add('hp-mid');
        } else {
            playerHpFill.classList.add('hp-low');
        }
    }

    // Update the dynamic combat info
    const mobNameEl = actionDiv.querySelector('.mob-name');
    const mobHpTextEl = actionDiv.querySelector('.mob-hp-text');
    const mobHpFill = actionDiv.querySelector('.mob-hp-fill');

    if (state.isDead) {
        // Player is dead - show respawn info
        const remaining = Math.ceil(state.respawnRemaining / 1000);
        if (mobNameEl) mobNameEl.textContent = '💀 Respawning...';
        if (mobHpTextEl) mobHpTextEl.textContent = `${remaining}s`;
        if (mobHpFill) {
            mobHpFill.style.width = `${100 - (state.respawnRemaining / 25000 * 100)}%`;
            mobHpFill.classList.add('respawn');
        }
    } else if (state.currentMob && state.inCombat) {
        // Fighting a mob
        const mobName = state.currentMob.name || 'Unknown';
        const hpPercent = state.mobMaxHp > 0 ? (state.mobCurrentHp / state.mobMaxHp) * 100 : 0;

        if (mobNameEl) mobNameEl.textContent = mobName;
        if (mobHpTextEl) mobHpTextEl.textContent = `${state.mobCurrentHp}/${state.mobMaxHp}`;
        if (mobHpFill) {
            mobHpFill.style.width = `${hpPercent}%`;
            mobHpFill.classList.remove('respawn');
            // Color based on mob HP
            mobHpFill.classList.remove('hp-high', 'hp-mid', 'hp-low');
            if (hpPercent > 50) {
                mobHpFill.classList.add('hp-high');
            } else if (hpPercent > 25) {
                mobHpFill.classList.add('hp-mid');
            } else {
                mobHpFill.classList.add('hp-low');
            }
        }
    } else if (state.isInKillDelay) {
        // Just killed a mob, waiting for next
        if (mobNameEl) mobNameEl.textContent = '✓ Killed!';
        if (mobHpTextEl) mobHpTextEl.textContent = 'Next mob...';
        if (mobHpFill) {
            mobHpFill.style.width = '0%';
            mobHpFill.classList.remove('respawn');
        }
    } else {
        // In combat but no current mob yet (searching for target)
        if (mobNameEl) mobNameEl.textContent = 'Searching...';
        if (mobHpTextEl) mobHpTextEl.textContent = '';
        if (mobHpFill) {
            mobHpFill.style.width = '0%';
            mobHpFill.classList.remove('respawn');
        }
    }
}

// Setup click handler for current action navigation (called once)
function setupCurrentActionHandler() {
    const actionDiv = document.getElementById('current-action');
    if (!actionDiv || actionDiv.dataset.handlerAttached) return;

    actionDiv.addEventListener('click', (e) => {
        const actionInfo = e.target.closest('.action-info[data-route]');
        if (actionInfo) {
            const route = actionInfo.dataset.route;
            const armorTier = actionInfo.dataset.armorTier;

            // Handle crafting specially - set internal state before navigating
            if (route && route.startsWith('crafting/') && window.craftingUI) {
                const subType = route.split('/')[1]; // 'jewelry' or 'ranged'
                window.craftingUI.switchCategory(subType);
                if (armorTier) {
                    window.craftingUI.switchArmorTier(parseInt(armorTier));
                }
                window.location.hash = '#/crafting';
                return;
            }

            // Set armor tier before navigating if applicable (for other skills)
            if (armorTier && window.craftingUI?.switchArmorTier) {
                window.craftingUI.switchArmorTier(parseInt(armorTier));
            }

            if (route) {
                window.location.hash = `#/${route}`;
            }
        }
    });

    actionDiv.dataset.handlerAttached = 'true';
}

/**
 * Update active bounty contracts display in sidebar
 * Uses incremental updates to preserve click handlers
 */
function updateActiveContractsDisplay() {
    const contractsDiv = document.getElementById('active-contracts');
    if (!contractsDiv) return;

    const activeContracts = bountySkill.getActiveContracts();

    // Update sidebar tab badge count and ready indicator
    const contractBadge = document.getElementById('sidebar-contract-count');
    if (contractBadge) {
        contractBadge.textContent = activeContracts.length.toString();

        // Check if any contract is ready to deliver
        const hasReadyContract = activeContracts.some(active =>
            bountySkill.canDeliverContract(active.contractId).success
        );
        contractBadge.classList.toggle('has-ready', hasReadyContract);
    }

    if (activeContracts.length === 0) {
        if (!contractsDiv.querySelector('.empty-message')) {
            contractsDiv.innerHTML = '<p class="empty-message">No active contracts</p>';
        }
        return;
    }

    // Check if we need to rebuild the structure (contract count changed or IDs changed)
    const existingContracts = contractsDiv.querySelectorAll('.sidebar-contract');
    // Only rebuild if contract count changes or contract IDs don't match
    // (incremental updates handle kill counts properly without full rebuild)
    let needsRebuild = existingContracts.length !== activeContracts.length;
    if (!needsRebuild) {
        // Check if contract IDs match
        for (let i = 0; i < activeContracts.length; i++) {
            const existingId = existingContracts[i]?.dataset.contractId;
            if (existingId !== activeContracts[i].contractId) {
                needsRebuild = true;
                break;
            }
        }
    }

    if (needsRebuild) {
        // Full rebuild needed
        const contractsHtml = activeContracts.map(active => {
            const contract = bountySkill.getContractDefinition(active.contractId);
            if (!contract) return '';

            const requirements = active.requirements || [];
            const canDeliver = bountySkill.canDeliverContract(active.contractId).success;

            const reqsHtml = requirements.map(req => {
                if (req.itemId) {
                    // Use bountySkill to get correct progress (items gathered since accepting)
                    const progress = bountySkill.getRequirementProgress(active.contractId, req.itemId);
                    const have = progress.current;
                    const need = progress.target;
                    const complete = have >= need;
                    // Cap displayed progress at target (no need to show 113/39)
                    const displayHave = Math.min(have, need);
                    const itemName = getItemName(req.itemId) || req.itemId.replace(/_/g, ' ');
                    const itemIcon = getItemIcon(req.itemId);
                    return `
                        <div class="contract-req ${complete ? 'complete' : ''}" data-item="${req.itemId}">
                            <span class="req-item"><span class="req-icon">${itemIcon}</span> ${itemName}</span>
                            <span class="req-count">${formatNumber(displayHave)}/${formatNumber(need)}</span>
                        </div>
                    `;
                } else if (req.mobId) {
                    const killed = active.progress[req.mobId] || 0;
                    const need = req.target;
                    const complete = killed >= need;
                    // Cap displayed progress at target
                    const displayKilled = Math.min(killed, need);
                    const mobName = req.mobId.replace(/_/g, ' ');
                    return `
                        <div class="contract-req ${complete ? 'complete' : ''}" data-mob="${req.mobId}">
                            <span class="req-item"><span class="req-icon">${getSkillIcon('combat')}</span> ${mobName}</span>
                            <span class="req-count">${displayKilled}/${need}</span>
                        </div>
                    `;
                }
                return '';
            }).join('');

            // Build rewards HTML with icons
            const rewards = active.rewards || {};
            const bountyXP = getBountyXP(contract);
            let rewardParts = [];
            if (rewards.gold) {
                rewardParts.push(`<span class="reward-gold">${getItemIcon('coins', rewards.gold)} ${formatNumber(rewards.gold)}</span>`);
            }
            if (rewards.xp) {
                for (const [skill, amount] of Object.entries(rewards.xp)) {
                    const icon = getSkillIcon(skill);
                    rewardParts.push(`<span class="reward-xp">${icon} ${formatNumber(amount)} XP</span>`);
                }
            }
            // Bounty XP with bounty skill icon
            rewardParts.push(`<span class="reward-xp">${getSkillIcon('bounty')} ${formatNumber(bountyXP)} XP</span>`);
            const rewardsText = rewardParts.join('');

            // Pilferer assist indicator
            const pilfererAssists = active.pilfererAssists || 0;
            const pilfererHtml = pilfererAssists > 0 ? `
                <span class="sidebar-pilferer-assist" title="Pilferers assisted ${pilfererAssists} time${pilfererAssists !== 1 ? 's' : ''}">
                    🗡️${pilfererAssists}
                </span>
            ` : '';

            // Build consume warning for deliver button (item requirements only)
            const itemReqs = requirements.filter(req => req.itemId);
            const consumeHtml = itemReqs.length > 0 ? itemReqs.map(req => {
                const icon = getItemIcon(req.itemId);
                return `<span class="sidebar-consume-item">${icon}-${formatNumber(req.target)}</span>`;
            }).join(' ') : '';

            return `
                <div class="sidebar-contract ${canDeliver ? 'ready' : ''}" data-contract-id="${active.contractId}">
                    <div class="contract-name-row">
                        <span class="contract-name">${contract.name}</span>
                        ${pilfererHtml}
                    </div>
                    <div class="contract-reqs">
                        ${reqsHtml}
                    </div>
                    <div class="contract-rewards-preview">${rewardsText}</div>
                    ${canDeliver ? `
                        <button class="contract-deliver-btn" data-deliver="${active.contractId}">
                            Deliver ${consumeHtml ? `<span class="sidebar-consume-warning">(${consumeHtml})</span>` : ''}
                        </button>
                    ` : ''}
                </div>
            `;
        }).join('');

        contractsDiv.innerHTML = contractsHtml;
    } else {
        // Incremental update - just update counts and states
        activeContracts.forEach(active => {
            const contractEl = contractsDiv.querySelector(`[data-contract-id="${active.contractId}"]`);
            if (!contractEl) return;

            const canDeliver = bountySkill.canDeliverContract(active.contractId).success;

            // Update ready state
            contractEl.classList.toggle('ready', canDeliver);

            // Update pilferer assist indicator
            const pilfererAssists = active.pilfererAssists || 0;
            const nameRow = contractEl.querySelector('.contract-name-row');
            let pilfererEl = nameRow?.querySelector('.sidebar-pilferer-assist');

            if (pilfererAssists > 0) {
                if (pilfererEl) {
                    // Update existing indicator
                    pilfererEl.innerHTML = `🗡️${pilfererAssists}`;
                    pilfererEl.title = `Pilferers assisted ${pilfererAssists} time${pilfererAssists !== 1 ? 's' : ''}`;
                } else if (nameRow) {
                    // Create new indicator
                    const newPilfererEl = document.createElement('span');
                    newPilfererEl.className = 'sidebar-pilferer-assist';
                    newPilfererEl.title = `Pilferers assisted ${pilfererAssists} time${pilfererAssists !== 1 ? 's' : ''}`;
                    newPilfererEl.innerHTML = `🗡️${pilfererAssists}`;
                    nameRow.appendChild(newPilfererEl);
                }
            } else if (pilfererEl) {
                // Remove indicator if no assists
                pilfererEl.remove();
            }

            // Update requirement counts
            const requirements = active.requirements || [];
            requirements.forEach(req => {
                if (req.itemId) {
                    const reqEl = contractEl.querySelector(`[data-item="${req.itemId}"]`);
                    if (reqEl) {
                        // Use bountySkill to get correct progress (items gathered since accepting)
                        const progress = bountySkill.getRequirementProgress(active.contractId, req.itemId);
                        const have = progress.current;
                        const need = progress.target;
                        const complete = have >= need;
                        // Cap displayed progress at target
                        const displayHave = Math.min(have, need);
                        reqEl.classList.toggle('complete', complete);
                        const countEl = reqEl.querySelector('.req-count');
                        if (countEl) {
                            countEl.textContent = `${formatNumber(displayHave)}/${formatNumber(need)}`;
                        }
                    }
                } else if (req.mobId) {
                    const reqEl = contractEl.querySelector(`[data-mob="${req.mobId}"]`);
                    if (reqEl) {
                        const killed = active.progress[req.mobId] || 0;
                        const need = req.target;
                        const complete = killed >= need;
                        // Cap displayed progress at target
                        const displayKilled = Math.min(killed, need);
                        reqEl.classList.toggle('complete', complete);
                        const countEl = reqEl.querySelector('.req-count');
                        if (countEl) {
                            countEl.textContent = `${formatNumber(displayKilled)}/${formatNumber(need)}`;
                        }
                    }
                }
            });

            // Update deliver button visibility
            const existingBtn = contractEl.querySelector('.contract-deliver-btn');
            if (canDeliver && !existingBtn) {
                // Build consume warning for deliver button
                const requirements = active.requirements || [];
                const itemReqs = requirements.filter(req => req.itemId);
                const consumeHtml = itemReqs.length > 0 ? itemReqs.map(req => {
                    const icon = getItemIcon(req.itemId);
                    return `<span class="sidebar-consume-item">${icon}-${formatNumber(req.target)}</span>`;
                }).join(' ') : '';

                const btn = document.createElement('button');
                btn.className = 'contract-deliver-btn';
                btn.dataset.deliver = active.contractId;
                btn.innerHTML = `Deliver ${consumeHtml ? `<span class="sidebar-consume-warning">(${consumeHtml})</span>` : ''}`;
                contractEl.appendChild(btn);
            } else if (!canDeliver && existingBtn) {
                existingBtn.remove();
            }
        });
    }
}

// Setup event delegation for contract delivery buttons
function setupContractDeliveryHandler() {
    const contractsDiv = document.getElementById('active-contracts');
    if (!contractsDiv || contractsDiv.dataset.handlerAttached) return;

    contractsDiv.addEventListener('click', (e) => {
        const btn = e.target.closest('.contract-deliver-btn');
        if (btn && btn.dataset.deliver) {
            e.preventDefault();
            e.stopPropagation();
            if (window.bountyUI && window.bountyUI.deliverContract) {
                window.bountyUI.deliverContract(btn.dataset.deliver);
            }
        }
    });

    contractsDiv.dataset.handlerAttached = 'true';
}

// updateInventoryDisplay is now imported from '../ui/inventoryUI.js'

/**
 * Update corruption visual effects
 * Adds/removes purple glow from all corrupted skills (multi-skill support)
 */
function updateCorruptionVisuals() {
    const corruptedSkills = getCorruptedSkillIds();
    const currentActionDiv = document.getElementById('current-action');

    // Remove corrupted and arcane-surge classes from all skill items in sidebar first
    document.querySelectorAll('.skill-item.corrupted, .skill-item.arcane-surge').forEach(el => {
        el.classList.remove('corrupted', 'arcane-surge');
    });

    // Remove from current action
    currentActionDiv?.classList.remove('corrupted', 'arcane-surge');

    if (corruptedSkills.length > 0) {
        // Add corruption glow to each corrupted skill in sidebar
        for (const skillId of corruptedSkills) {
            const skillItem = document.querySelector(`.skill-item[data-skill="${skillId}"]`);
            if (skillItem) {
                const info = getSkillCorruptionInfo(skillId);
                if (info.arcaneSurge) {
                    skillItem.classList.add('arcane-surge');
                } else {
                    skillItem.classList.add('corrupted');
                }
            }
        }

        // Add corruption glow to current action if its skill is corrupted
        const currentAction = gameState.getCurrentAction();
        if (currentAction && corruptedSkills.includes(currentAction.type)) {
            const actionInfo = getSkillCorruptionInfo(currentAction.type);
            if (actionInfo.arcaneSurge) {
                currentActionDiv?.classList.add('arcane-surge');
            } else {
                currentActionDiv?.classList.add('corrupted');
            }
        }

        // Update corruption indicator in skill header (if viewing a corrupted skill)
        const currentSkillId = getCurrentSkillId();
        if (currentSkillId && corruptedSkills.includes(currentSkillId)) {
            const skillCorruptionInfo = getSkillCorruptionInfo(currentSkillId);
            updateSkillCorruptionIndicator(skillCorruptionInfo);
        } else {
            hideSkillCorruptionIndicator();
        }
    } else {
        // Hide any corruption indicators
        hideSkillCorruptionIndicator();
    }
}

/**
 * Update or show corruption indicator in skill header
 * @param {Object} skillCorruptionInfo - Corruption info for the current skill
 */
function updateSkillCorruptionIndicator(skillCorruptionInfo) {
    if (!skillCorruptionInfo || !skillCorruptionInfo.active) {
        hideSkillCorruptionIndicator();
        return;
    }

    // Find or create corruption indicator in skill header (supports both old and new header styles)
    let indicator = document.querySelector('.skill-corruption-indicator');
    const skillHeader = document.querySelector('.skill-header') || document.querySelector('.compact-header');

    if (!skillHeader) return;

    if (!indicator) {
        // Create the indicator
        indicator = document.createElement('div');
        indicator.className = 'skill-corruption-indicator';
    }

    // Determine indicator variant
    const isCombat = skillCorruptionInfo.skillId === 'combat';
    const isArcaneSurge = skillCorruptionInfo.arcaneSurge;
    const variant = isCombat ? 'combat' : isArcaneSurge ? 'arcane' : 'normal';

    // Only rebuild innerHTML when variant changes (prevents tooltip flicker on hover)
    if (indicator.dataset.variant !== variant) {
        indicator.dataset.variant = variant;
        indicator.classList.toggle('arcane-surge', isArcaneSurge);

        indicator.innerHTML = isCombat
            ? `<span class="corruption-label">💀 CORRUPTED PREY</span>
               <span class="corruption-time"></span>
               <div class="corruption-tooltip">
                   <div class="tooltip-title">Corrupted Prey</div>
                   <div class="tooltip-section">Temporary Debuffs:</div>
                   <div class="tooltip-row">💀 +30% Damage Taken</div>
                   <div class="tooltip-row">🛡️ -25% Mob Defense</div>
                   <div class="tooltip-row">🔮 25% Glyph Save</div>
                   <div class="tooltip-section">Persistent Marks:</div>
                   <div class="tooltip-row">📦 2x Loot Drops</div>
                   <div class="tooltip-row">✨ 1.5x XP</div>
                   <div class="tooltip-note">Marks clear on zone switch</div>
               </div>`
            : isArcaneSurge
            ? `<span class="corruption-label">✨ ARCANE SURGE</span>
               <span class="corruption-time"></span>
               <div class="corruption-tooltip">
                   <div class="tooltip-title">Arcane Surge</div>
                   <div class="tooltip-row">✨ 150% XP Gains</div>
                   <div class="tooltip-row">⚡ 120% Faster Actions</div>
                   <div class="tooltip-row">📦 4x Loot Drops</div>
                   <div class="tooltip-row">${getItemIcon('coins')} Free Gold Costs</div>
                   <div class="tooltip-row">🔮 75% Resource Save</div>
                   <div class="tooltip-note">Shorter duration, 3x power</div>
               </div>`
            : `<span class="corruption-label">⚡ CORRUPTED</span>
               <span class="corruption-time"></span>
               <div class="corruption-tooltip">
                   <div class="tooltip-title">Corruption Buffs</div>
                   <div class="tooltip-row">✨ 50% XP Gains</div>
                   <div class="tooltip-row">⚡ 40% Faster Actions</div>
                   <div class="tooltip-row">📦 2x Loot Drops</div>
                   <div class="tooltip-row">${getItemIcon('coins')} Free Gold Costs</div>
                   <div class="tooltip-row">🔮 25% Resource Save</div>
               </div>`;
    }

    if (!indicator.parentElement) {
        // Insert after header title (h2 for old style, .compact-title for new style)
        const headerTitle = skillHeader.querySelector('h2') || skillHeader.querySelector('.compact-title');
        if (headerTitle) {
            headerTitle.after(indicator);
        } else {
            skillHeader.prepend(indicator);
        }
    }

    // Update time only (no innerHTML rebuild)
    const seconds = skillCorruptionInfo.remainingSeconds;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    const timeStr = minutes > 0 ? `${minutes}:${secs.toString().padStart(2, '0')}` : `${secs}s`;
    indicator.querySelector('.corruption-time').textContent = timeStr;
    indicator.classList.remove('hidden');
}

/**
 * Hide corruption indicator
 */
function hideSkillCorruptionIndicator() {
    const indicator = document.querySelector('.skill-corruption-indicator');
    if (indicator) {
        indicator.classList.add('hidden');
    }
}

/**
 * Start the game loop
 */
export function startGameLoop() {
    if (gameLoopInterval) {
        logger.warn('Game loop already running');
        return;
    }

    logger.info(`Starting game loop (${GAME_CONFIG.TICK_RATE}ms tick rate)`);
    gameLoopInterval = setInterval(gameTick, GAME_CONFIG.TICK_RATE);
}

/**
 * Stop the game loop
 */
export function stopGameLoop() {
    if (gameLoopInterval) {
        clearInterval(gameLoopInterval);
        gameLoopInterval = null;
        logger.info('Game loop stopped');
    }
}

export default {
    startGameLoop,
    stopGameLoop,
};
